import React, { useEffect, useState } from "react";
import { Image, Plus, Send, Trash2 } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { toast } from "../../components/ui/sonner";
import { useStudioAuth } from "../StudioAuthContext";
import { EmptyState, FieldLabel, OPS_INPUT, Panel } from "../ui";

const builtIn = new Set(["storm-desk-live", "severe-update", "breaking-weather"]);
const accents = { blue: "border-sky-500", amber: "border-amber-400", red: "border-red-500" };

export const GraphicsSection = () => {
  const { request } = useStudioAuth();
  const [graphics, setGraphics] = useState([]);
  const [form, setForm] = useState({ name: "", title: "", subtitle: "", kind: "lower_third", accent: "blue" });

  const load = async () => {
    const response = await request("/operations/graphics");
    if (response.ok) setGraphics(await response.json());
  };
  useEffect(() => { load(); }, [request]); // eslint-disable-line react-hooks/exhaustive-deps

  const create = async (event) => {
    event.preventDefault();
    const response = await request("/operations/graphics", { method: "POST", body: JSON.stringify(form) });
    if (!response.ok) return toast.error("Graphic could not be saved");
    setForm({ name: "", title: "", subtitle: "", kind: "lower_third", accent: "blue" });
    await load();
    toast.success("Graphic saved");
  };

  const prepare = async (graphic) => {
    const response = await request("/broadcast", { method: "POST", body: JSON.stringify({ lower_third_title: graphic.title, lower_third_subtitle: graphic.subtitle || "" }) });
    if (response.ok) toast.success("Graphic staged for Preview");
    else toast.error("Graphic could not be staged");
  };

  const remove = async (graphicId) => {
    const response = await request(`/operations/graphics/${graphicId}`, { method: "DELETE" });
    if (response.ok) setGraphics((items) => items.filter((item) => item.id !== graphicId));
  };

  return (
    <div className="grid gap-3 xl:grid-cols-[1fr_320px]">
      <Panel title="Templates" icon={Image} note={`${graphics.length} templates`} bodyClass="p-0">
        {graphics.length === 0 ? (
          <EmptyState icon={Image} title="No graphics yet" message="Build a lower third template to stage it on Preview in one click." testId="graphics-empty-state" />
        ) : (
          <div className="grid gap-px bg-[rgba(120,170,255,0.12)] sm:grid-cols-2 2xl:grid-cols-3">
            {graphics.map((graphic) => (
              <article key={graphic.id} data-testid={`graphic-${graphic.id}`} className="bg-[#0a0f1d]">
                <div className="relative aspect-video bg-[#04060e] p-3">
                  <div className={`absolute bottom-3 left-3 right-3 border-l-4 ${accents[graphic.accent] || accents.blue} bg-black/85 px-3 py-2`}>
                    <div className="display truncate text-base font-black uppercase text-white">{graphic.title}</div>
                    <div className="truncate text-[11px] text-slate-300">{graphic.subtitle}</div>
                  </div>
                </div>
                <div className="flex items-center justify-between gap-2 border-t border-[rgba(120,170,255,0.12)] p-2.5">
                  <div className="min-w-0">
                    <div className="truncate text-xs font-bold text-white">{graphic.name}</div>
                    <div className="mono text-[9px] uppercase tracking-widest text-slate-600">{graphic.kind}</div>
                  </div>
                  <div className="flex shrink-0 gap-1">
                    {!builtIn.has(graphic.id) && (
                      <button data-testid={`delete-graphic-${graphic.id}`} onClick={() => remove(graphic.id)} title="Delete graphic" className="grid h-8 w-8 place-items-center text-slate-500 transition-colors hover:bg-red-500/10 hover:text-red-400"><Trash2 className="h-4 w-4" /></button>
                    )}
                    <button data-testid={`prepare-graphic-${graphic.id}`} onClick={() => prepare(graphic)} title="Stage graphic" className="grid h-8 w-8 place-items-center border border-sky-500/30 text-sky-300 transition-colors hover:bg-sky-500/15"><Send className="h-4 w-4" /></button>
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}
      </Panel>

      <Panel title="New Graphic" icon={Plus} className="h-fit" bodyClass="p-4">
        <form onSubmit={create} className="space-y-3">
          <div className="space-y-1">
            <FieldLabel>Template name</FieldLabel>
            <Input data-testid="graphic-name-input" value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} placeholder="Severe update" required className={OPS_INPUT} />
          </div>
          <div className="space-y-1">
            <FieldLabel>Headline</FieldLabel>
            <Input data-testid="graphic-title-input" value={form.title} onChange={(event) => setForm({ ...form, title: event.target.value })} placeholder="TORNADO WARNING" required className={OPS_INPUT} />
          </div>
          <div className="space-y-1">
            <FieldLabel>Subtitle</FieldLabel>
            <Input data-testid="graphic-subtitle-input" value={form.subtitle} onChange={(event) => setForm({ ...form, subtitle: event.target.value })} placeholder="Cleveland County" className={OPS_INPUT} />
          </div>
          <div className="space-y-1.5">
            <FieldLabel>Accent</FieldLabel>
            <div className="grid grid-cols-3 gap-1">
              {["blue", "amber", "red"].map((accent) => (
                <button type="button" key={accent} data-testid={`graphic-accent-${accent}`} onClick={() => setForm({ ...form, accent })}
                  className={`h-8 border text-[10px] font-bold uppercase tracking-wider transition-colors ${form.accent === accent ? "border-sky-500 bg-sky-500/15 text-white" : "border-white/10 text-slate-500 hover:text-slate-200"}`}>
                  {accent}
                </button>
              ))}
            </div>
          </div>
          <Button data-testid="save-graphic-button" type="submit" className="h-9 w-full rounded-none bg-[#1a5cff] text-[11px] font-bold uppercase tracking-widest hover:bg-[#2a6cff]">
            <Plus className="h-4 w-4" /> Save Graphic
          </Button>
        </form>
      </Panel>
    </div>
  );
};

export default GraphicsSection;
